/**
 * Digitization Slice
 *
 * Manages state for layer creation, feature digitization, snapping,
 * and copy/paste operations.
 *
 * Validates: Requirements 47, 48, 49
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  SnappingConfig,
  ClipboardFeature,
  DigitizationMode,
  LayerGeometryType,
  AttributeField,
} from '../../types/layerCreation';
import { DEFAULT_SNAPPING_CONFIG } from '../../types/layerCreation';

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

export interface DigitizationState {
  /** Current digitization mode */
  mode: DigitizationMode;
  /** ID of the layer being digitized */
  activeDigitizationLayerId: string | null;
  /** Geometry type of the active digitization layer */
  activeGeometryType: LayerGeometryType | null;
  /** Attribute fields for the active digitization layer */
  activeAttributeFields: AttributeField[];
  /** Pending geometry waiting for attribute input */
  pendingGeometry: GeoJSON.Geometry | null;
  /** Snapping configuration */
  snapping: SnappingConfig;
  /** Features in clipboard */
  clipboard: ClipboardFeature[];
  /** Whether the create layer dialog is open */
  createLayerDialogOpen: boolean;
  /** Whether the attribute input dialog is open */
  attributeInputDialogOpen: boolean;
  /** Whether the snapping config dialog is open */
  snappingConfigDialogOpen: boolean;
  /** Error message */
  error: string | null;
}

const initialState: DigitizationState = {
  mode: 'idle',
  activeDigitizationLayerId: null,
  activeGeometryType: null,
  activeAttributeFields: [],
  pendingGeometry: null,
  snapping: { ...DEFAULT_SNAPPING_CONFIG },
  clipboard: [],
  createLayerDialogOpen: false,
  attributeInputDialogOpen: false,
  snappingConfigDialogOpen: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const digitizationSlice = createSlice({
  name: 'digitization',
  initialState,
  reducers: {
    /** Set the digitization mode */
    setDigitizationMode: (state, action: PayloadAction<DigitizationMode>) => {
      state.mode = action.payload;
    },

    /** Activate digitization for a layer */
    startDigitization: (
      state,
      action: PayloadAction<{
        layerId: string;
        geometryType: LayerGeometryType;
        attributeFields: AttributeField[];
      }>
    ) => {
      state.activeDigitizationLayerId = action.payload.layerId;
      state.activeGeometryType = action.payload.geometryType;
      state.activeAttributeFields = action.payload.attributeFields;
      state.mode = 'drawing';
      state.error = null;
    },

    /** Stop digitization */
    stopDigitization: (state) => {
      state.activeDigitizationLayerId = null;
      state.activeGeometryType = null;
      state.activeAttributeFields = [];
      state.pendingGeometry = null;
      state.mode = 'idle';
    },

    /** Set pending geometry (after draw completes, before attribute input) */
    setPendingGeometry: (state, action: PayloadAction<GeoJSON.Geometry | null>) => {
      state.pendingGeometry = action.payload;
      if (action.payload) {
        state.mode = 'attribute-input';
        state.attributeInputDialogOpen = true;
      }
    },

    /** Clear pending geometry (after save or cancel) */
    clearPendingGeometry: (state) => {
      state.pendingGeometry = null;
      state.attributeInputDialogOpen = false;
      state.mode = state.activeDigitizationLayerId ? 'drawing' : 'idle';
    },

    /** Update snapping configuration */
    setSnappingConfig: (state, action: PayloadAction<SnappingConfig>) => {
      state.snapping = action.payload;
    },

    /** Toggle snapping enabled/disabled */
    toggleSnapping: (state) => {
      state.snapping.enabled = !state.snapping.enabled;
    },

    /** Set snapping tolerance */
    setSnappingTolerance: (state, action: PayloadAction<number>) => {
      state.snapping.tolerance = action.payload;
    },

    /** Toggle snap to vertices */
    toggleSnapToVertices: (state) => {
      state.snapping.snapToVertices = !state.snapping.snapToVertices;
    },

    /** Toggle snap to edges */
    toggleSnapToEdges: (state) => {
      state.snapping.snapToEdges = !state.snapping.snapToEdges;
    },

    /** Toggle snap to intersections */
    toggleSnapToIntersections: (state) => {
      state.snapping.snapToIntersections = !state.snapping.snapToIntersections;
    },

    /** Copy features to clipboard */
    copyToClipboard: (state, action: PayloadAction<ClipboardFeature[]>) => {
      state.clipboard = action.payload;
    },

    /** Clear clipboard */
    clearClipboard: (state) => {
      state.clipboard = [];
    },

    /** Open/close create layer dialog */
    setCreateLayerDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.createLayerDialogOpen = action.payload;
    },

    /** Open/close attribute input dialog */
    setAttributeInputDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.attributeInputDialogOpen = action.payload;
    },

    /** Open/close snapping config dialog */
    setSnappingConfigDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.snappingConfigDialogOpen = action.payload;
    },

    /** Set error */
    setDigitizationError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setDigitizationMode,
  startDigitization,
  stopDigitization,
  setPendingGeometry,
  clearPendingGeometry,
  setSnappingConfig,
  toggleSnapping,
  setSnappingTolerance,
  toggleSnapToVertices,
  toggleSnapToEdges,
  toggleSnapToIntersections,
  copyToClipboard,
  clearClipboard,
  setCreateLayerDialogOpen,
  setAttributeInputDialogOpen,
  setSnappingConfigDialogOpen,
  setDigitizationError,
} = digitizationSlice.actions;

export default digitizationSlice.reducer;
